import { useEffect, useRef } from 'react';
import config from '../config';
import { OnboardDeviceOnlineStatusWsPayload } from '../interfaces';
import { parseOnboardDeviceOnlineStatusWsPayload } from '../utils/onboardDeviceOnlineStatus';

interface UseOnboardDevicesStatusWebSocketProps {
    enabled: boolean;
    onStatusUpdate: (payload: OnboardDeviceOnlineStatusWsPayload) => void;
    onConnected?: () => void;
}

const WS_RECONNECT_TIMEOUT_MS = 3000;

export const useOnboardDevicesStatusWebSocket = ({
    enabled,
    onStatusUpdate,
    onConnected,
}: UseOnboardDevicesStatusWebSocketProps) => {
    const onStatusUpdateRef = useRef(onStatusUpdate);
    const onConnectedRef = useRef(onConnected);

    useEffect(() => {
        onStatusUpdateRef.current = onStatusUpdate;
    }, [onStatusUpdate]);

    useEffect(() => {
        onConnectedRef.current = onConnected;
    }, [onConnected]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const wsBaseUrl = config.ONBOARD_DEVICES_STATUS_WS_URL;

        if (!wsBaseUrl) {
            console.warn(
                'REACT_APP_ONBOARD_DEVICES_STATUS_WS_URL is not configured. Realtime updates are disabled.',
            );
            return;
        }

        let socket: WebSocket | null = null;
        let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
        let isDisposed = false;
        let shouldReconnect = true;

        const connect = () => {
            try {
                socket = new WebSocket(wsBaseUrl);
            } catch (error) {
                console.error('Failed to initialize onboard devices status websocket.', error);
                if (shouldReconnect && !isDisposed) {
                    reconnectTimeoutId = setTimeout(connect, WS_RECONNECT_TIMEOUT_MS);
                }
                return;
            }

            socket.onmessage = (event) => {
                const payload = parseOnboardDeviceOnlineStatusWsPayload(event.data as string);

                if (!payload) {
                    return;
                }

                onStatusUpdateRef.current(payload);
            };

            socket.onopen = () => {
                onConnectedRef.current?.();
            };

            socket.onerror = (event) => {
                console.error('Onboard devices status websocket error.', event);
            };

            socket.onclose = () => {
                if (!shouldReconnect || isDisposed) {
                    return;
                }

                reconnectTimeoutId = setTimeout(connect, WS_RECONNECT_TIMEOUT_MS);
            };
        };

        connect();

        return () => {
            isDisposed = true;
            shouldReconnect = false;

            if (reconnectTimeoutId) {
                clearTimeout(reconnectTimeoutId);
            }

            if (
                socket &&
                (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
            ) {
                socket.close();
            }
        };
    }, [enabled]);
};
